import React from 'react';
import { Truck } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { validDays } from '@/components/ewayBill/shared';
import { TransporterPick } from '@/components/ewayBill/TransporterPick';

const VEH_RX = /^[A-Z]{2}\d{1,2}[A-Z]{0,3}\d{4}$/;
const TRANS_ID_RX = /^\d{2}[A-Z]{5}\d{4}[A-Z][0-9A-Z]{3}$/;

/** Same Part B rules as the server, shown next to the fields. */
export const partBProblems = (v) => {
  const veh = (v.vehicle_no || '').replace(/[^A-Za-z0-9]/g, '').toUpperCase();
  const tid = (v.transporter_id || '').trim().toUpperCase();
  return {
    vehicle: !veh && !tid ? 'Enter the vehicle number (or a transporter ID)' : veh && !VEH_RX.test(veh) ? 'Not a valid vehicle number (e.g. TN30AB1234)' : '',
    distance: (Number(v.distance_km) || 0) > 4000 ? 'Distance must be 0–4000 km' : '',
    transporter: tid && !TRANS_ID_RX.test(tid) ? 'Transporter ID must be a 15-character GSTIN / TRANSIN' : '',
  };
};
const Err = ({ msg, testId }) => (msg ? <p className="text-xs text-rose-600" data-testid={testId}>{msg}</p> : null);

const SRC = { calculated: 'calculated', remembered: 'remembered', same_pin: 'same PIN code' };
const DistNote = ({ dist, value }) => {
  if (!dist) return null;
  if (dist.problem && !value) return <p className="text-xs text-amber-700" data-testid="ewb-distance-source">{dist.problem}</p>;
  if (dist.km == null) return null;
  const pins = `PIN ${dist.from_pin} → ${dist.to_pin}`;
  const txt = String(dist.km) !== String(value) ? `changed — WMS remembers your figure for ${pins}`
    : dist.source === 'entered' ? `entered by ${dist.by_name || '—'} · ${pins}` : `${SRC[dist.source] || dist.source} · ${pins}`;
  return <p className="text-xs text-gray-500" data-testid="ewb-distance-source">{txt}</p>;
};

/** Step 2b — Part B: transporter + PIN-to-PIN distance come pre-filled; an open Transport trip fills the vehicle number. */
export const PartBForm = ({ value: v, onChange, trips, dist }) => {
  const err = partBProblems(v);
  const set = (k) => (e) => onChange({
    ...v, [k]: k === 'distance_km' ? e.target.value.replace(/\D/g, '') : e.target.value, ...(k === 'vehicle_no' ? { trip_id: null } : {}),
  });
  const pickTrip = (id) => {
    const t = trips.find((x) => x.trip_id === id);
    onChange(t ? { ...v, trip_id: id, vehicle_no: t.vehicle_no, distance_km: v.distance_km || (t.distance_km != null ? String(t.distance_km) : '') } : { ...v, trip_id: null });
  };
  return (
    <div className="rounded-lg border border-gray-200 bg-white p-3 space-y-3" data-testid="ewb-partb">
      <div className="flex items-center gap-2 text-sm font-semibold"><Truck className="w-4 h-4 text-[#2D5F4F]" />Part B — vehicle (road)</div>
      <div className="space-y-1">
        <Label className="text-xs">Open Transport trip (fills the vehicle number)</Label>
        <select value={v.trip_id || ''} onChange={(e) => pickTrip(e.target.value)} className="h-10 w-full rounded-md border border-gray-300 bg-white px-3 text-sm" data-testid="ewb-trip-select">
          <option value="">— No trip / hired vehicle —</option>
          {trips.map((t) => (
            <option key={t.trip_id} value={t.trip_id}>{t.vehicle_label} · {t.route} · {t.status}{t.distance_km != null ? ` · ~${t.distance_km} km` : ''} · {t.date}</option>
          ))}
        </select>
      </div>
      <div className="grid md:grid-cols-2 xl:grid-cols-4 gap-3">
        <div className="space-y-1"><Label className="text-xs">Vehicle number</Label>
          <Input value={v.vehicle_no} onChange={set('vehicle_no')} placeholder="TN30AB1234" className="font-mono uppercase" data-testid="ewb-vehicle-no" />
          <Err msg={err.vehicle} testId="ewb-vehicle-error" /></div>
        <div className="space-y-1"><Label className="text-xs">Distance (km)</Label>
          <Input value={v.distance_km} onChange={set('distance_km')} inputMode="numeric" placeholder="0" data-testid="ewb-distance" />
          <Err msg={err.distance} testId="ewb-distance-error" /><DistNote dist={dist} value={v.distance_km} /></div>
        <TransporterPick v={v} onChange={onChange} err={err.transporter ? <Err msg={err.transporter} testId="ewb-transporter-error" /> : null} />
      </div>
      <p className="text-xs text-gray-500" data-testid="ewb-validity-hint">
        Valid for <b>{validDays(v.distance_km)} day{validDays(v.distance_km) > 1 ? 's' : ''}</b> (1 day per 200 km) — ends at midnight. With a Transporter ID the vehicle number can be added later.
      </p>
    </div>
  );
};

export default PartBForm;
